import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import ProtectedRoute from './pages/Protectedroute';
import ErrorBoundary from './components/ErrorBoundary';
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import { logger } from './utils/logger';

function lazyWithPreload(factory) {
  const Component = lazy(factory);
  Component.preload = factory;
  return Component;
}

const DashboardPage = lazyWithPreload(() => import('./pages/DashboardPage'));
const MobileApp = lazyWithPreload(() => import('./pages/MobileApp'));
const OfficialPage = lazyWithPreload(() => import('./pages/OfficialPage'));

// ─── Responsive Home (shows dashboard on desktop, mobile on phone) ────────────
function ResponsiveHome() {
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 1100);

  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1100);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return isMobile ? <MobileApp /> : <DashboardPage />;
}

// ─── Responsive Official (shows admin panel on desktop, mobile app on zoom/phone) ──
function ResponsiveOfficial() {
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 1100);

  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1100);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return isMobile ? <MobileApp /> : <OfficialPage />;
}

function RouteContent() {
  return (
    <Routes>
      {/* Public: Home page (dashboard) — no auth needed */}
      <Route path="/" element={<ResponsiveHome />} />

      {/* Public: Login page for officials */}
      <Route path="/login" element={<LoginPage />} />

      {/* Public: Forgot password */}
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />

      {/* Public: Custom Firebase password reset handler */}
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      {/* Protected: Official admin page */}
      <Route
        path="/official"
        element={(
          <ProtectedRoute>
            <ResponsiveOfficial />
          </ProtectedRoute>
        )}
      />

      {/* Catch-all: unknown URLs go to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  // Register the UNIFIED service worker (push + app-shell caching) early and
  // unconditionally so it is ready before any push arrives — required for
  // reliable Android/iOS background notification delivery.
  //
  // This is now the ONE AND ONLY service worker. The legacy /service-worker.js
  // has been neutralized to avoid scope conflicts at "/". See
  // client/src/index.js → cleanupLegacyServiceWorker() and
  // client/public/service-worker.js for the self-unregister stub.
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return undefined;
    // In dev, webpack serves un-hashed chunk names (e.g. main.chunk.js).
    // The SW's cacheFirst strategy caches these and serves stale code on
    // subsequent loads, which is why OfficialPage (and other lazy chunks)
    // show old designs until multiple refreshes clear the cache.
    // The SW is only useful in production where webpack content-hashes
    // every filename, so cache-busting happens automatically.
    if (process.env.NODE_ENV !== 'production') return undefined;

    let updateInterval;

    navigator.serviceWorker
      .register('/firebase-messaging-sw.js', { scope: '/' })
      .then((reg) => {
        logger.log('App', 'FCM SW registered');

        // Check for SW updates every hour so long-lived PWA sessions pick up
        // new SW versions (e.g. after a deploy) without the user having to
        // close the app entirely.
        updateInterval = setInterval(() => {
          reg.update().catch(() => {});
        }, 60 * 60 * 1000);

        // When a new SW is waiting, ask it to skip waiting so updates apply
        // on next page load (or immediately if no tabs hold a client).
        if (reg.waiting) {
          reg.waiting.postMessage({ type: 'SKIP_WAITING' });
        }
        reg.addEventListener('updatefound', () => {
          const newWorker = reg.installing;
          if (!newWorker) return;
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              newWorker.postMessage({ type: 'SKIP_WAITING' });
            }
          });
        });
      })
      .catch((err) => logger.error('App', 'FCM SW registration failed', err?.message));

    // Listen for messages from the SW — specifically the subscription-expired
    // signal (emitted by pushsubscriptionchange) so we can silently re-register
    // the FCM token without user interaction.
    const onSWMessage = async (event) => {
      if (event.data?.type === 'FCM_SUBSCRIPTION_EXPIRED') {
        logger.warn('App', 'SW reports push subscription expired — re-registering token');
        try {
          const mod = await import('./utils/notificationManager');
          await mod.forceReRegister?.();
        } catch (e) {
          logger.error('App', 'Re-register failed', e?.message);
        }
      }
    };
    navigator.serviceWorker.addEventListener('message', onSWMessage);

    // When the active SW changes (e.g. after an update claim), reload once so
    // all open clients start using the new SW's push handler.
    // Skip in development — index.js unregisters all SWs on every load, which
    // would trigger controllerchange on every registration → infinite reload loop.
    let hasReloaded = false;
    const onControllerChange = () => {
      if (process.env.NODE_ENV !== 'production') return;
      if (hasReloaded) return;
      hasReloaded = true;
      setTimeout(() => window.location.reload(), 500);
    };
    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);

    return () => {
      clearInterval(updateInterval);
      navigator.serviceWorker.removeEventListener('message', onSWMessage);
      navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const isLoginRoute = window.location.pathname.startsWith('/login');
    if (isLoginRoute) return undefined;

    const preloadRoutes = () => {
      DashboardPage.preload?.();
      MobileApp.preload?.();
    };

    if ('requestIdleCallback' in window) {
      const idleId = window.requestIdleCallback(() => preloadRoutes(), { timeout: 2000 });
      return () => window.cancelIdleCallback?.(idleId);
    }

    const timer = window.setTimeout(preloadRoutes, 1200);
    return () => window.clearTimeout(timer);
  }, []);

  const pageLoader = <PageLoader />;

  return (
    <Router>
      <ErrorBoundary>
        <Suspense fallback={pageLoader}>
          <RouteContent />
        </Suspense>
      </ErrorBoundary>
    </Router>
  );
}

// Route-aware Suspense fallback. Reads the current path on each render so the
// label matches the page actually being loaded (dashboard for citizens, admin
// portal only for /official) instead of always saying "Admin Portal".
function getLoaderCopy(pathname) {
  if (pathname.startsWith('/official')) {
    return { title: 'Nilo-load ang Admin Portal...', subtitle: 'Handa na ang lahat' };
  }
  if (pathname.startsWith('/login')) {
    return { title: 'Nilo-load ang Login...', subtitle: 'Sandali lang po' };
  }
  return { title: 'Nilo-load ang Dashboard...', subtitle: 'Sandali lang po' };
}

function PageLoader() {
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '/';
  const { title, subtitle } = getLoaderCopy(pathname);

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #C62828 0%, #8B1A1A 40%, #3D0A0A 100%)',
      fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
    }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{
          fontSize: 32,
          marginBottom: 16,
          animation: 'pulse 1.5s infinite',
        }}>
          ⏳
        </div>
        <div style={{
          fontSize: 16,
          color: '#fff',
          fontWeight: 600,
          marginBottom: 8,
        }}>
          {title}
        </div>
        <div style={{
          fontSize: 12,
          color: 'rgba(255,255,255,0.7)',
          marginBottom: 24,
        }}>
          {subtitle}
        </div>
        <div style={{
          width: 200,
          height: 4,
          background: 'rgba(255,255,255,0.2)',
          borderRadius: 2,
          overflow: 'hidden',
        }}>
          <div style={{
            height: '100%',
            background: '#fff',
            borderRadius: 2,
            animation: 'slide 1.5s infinite',
            width: '30%',
          }} />
        </div>
      </div>
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.6; }
        }
        @keyframes slide {
          0% { transform: translateX(-200px); }
          100% { transform: translateX(200px); }
        }
      `}</style>
    </div>
  );
}

export default App;